import type { FlatNode, NestedNode, OrderedGroup } from "screens/call/callscript-graph-to-nested";
import SaleIcon from "assets/sale.svg?react";
import ComplianceIcon from "assets/compliance.svg?react";
import VerbatimIcon from "assets/verbatim.svg?react";

import { TimedProgressBar } from "./progress-bar";
import styles from "./index.module.css";
import { useEffect, useRef } from "react";
import clsx from "clsx";

const ElementType = {
  STATEMENT: 1,
  QUESTION: 2,
  OPTION: 32,
  START: 64,
} as const;

type NodeProps = {
  data: NestedNode[];
  currentNodeId?: number | null;
  completedNodeIds?: Set<number>;
  flattenedGraph?: FlatNode[];
  orderedGroups?: OrderedGroup[];
  onSelectOption?: (id: string) => void;
  onProceed?: (text: string) => void;
  autoFoundNodeId?: number | null;
};

const getNodesInGroup = (groupId: number, flattenedGraph: FlatNode[]): FlatNode[] =>
  flattenedGraph.filter((node) => node.groupId === groupId);

const getIsGroupCompleted = (
  groupId: number,
  flattenedGraph: FlatNode[],
  completedNodeIds: Set<number>,
): boolean => {
  const nodesInGroup = getNodesInGroup(groupId, flattenedGraph);
  if (nodesInGroup.length === 0) {
    return false;
  }
  const optionNodes = nodesInGroup.filter((node) => node.type === "option");
  const nonOptionNodes = nodesInGroup.filter((node) => node.type !== "option");
  const optionsCompleted =
    optionNodes.length === 0 || optionNodes.some((node) => completedNodeIds.has(node.id));
  const nonOptionsCompleted =
    nonOptionNodes.length === 0 || nonOptionNodes.every((node) => completedNodeIds.has(node.id));
  return optionsCompleted && nonOptionsCompleted;
};

const getCurrentActiveGroup = (
  currentNodeId: number | null | undefined,
  flattenedGraph: FlatNode[],
): number | null => {
  if (!currentNodeId) {
    return null;
  }
  const currentNode = flattenedGraph.find((node) => node.id === currentNodeId);
  return currentNode?.groupId ?? null;
};

const isGroupBeforeCurrent = (
  groupId: number,
  currentActiveGroupId: number | null,
  orderedGroups: OrderedGroup[],
): boolean => {
  if (!currentActiveGroupId) {
    return false;
  }

  const targetGroup = orderedGroups.find((g) => g.id === groupId);
  const currentGroup = orderedGroups.find((g) => g.id === currentActiveGroupId);

  if (!(targetGroup && currentGroup)) {
    return false;
  }

  return targetGroup.order < currentGroup.order;
};

export function Node({
  data,
  currentNodeId,
  completedNodeIds,
  flattenedGraph = [],
  orderedGroups = [],
  onSelectOption,
  onProceed,
  autoFoundNodeId,
}: NodeProps) {
  const currentRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentNodeId]);

  if (data.length === 0) {
    return null;
  }
  const isSingleNode = data.length === 1;
  const isFound = currentNodeId ? autoFoundNodeId === currentNodeId : false;

  if (isSingleNode) {
    const node = data[0];

    if (node.elementType === ElementType.START) {
      return (
        <>
          <Node
            autoFoundNodeId={autoFoundNodeId}
            completedNodeIds={completedNodeIds}
            currentNodeId={currentNodeId}
            data={node.children}
            flattenedGraph={flattenedGraph}
            onProceed={onProceed}
            onSelectOption={onSelectOption}
            orderedGroups={orderedGroups}
          />
        </>
      );
    }

    if (node.elementType === ElementType.STATEMENT || node.elementType === ElementType.QUESTION) {
      const isCurrentNode = currentNodeId === node.id;

      const currentActiveGroupId = getCurrentActiveGroup(currentNodeId, flattenedGraph);
      let isGroupCompleted = false;
      let isGroupMissed = false;

      if (node.groupId && completedNodeIds) {
        isGroupCompleted = getIsGroupCompleted(node.groupId, flattenedGraph, completedNodeIds);
        const isBeforeCurrent = isGroupBeforeCurrent(
          node.groupId,
          currentActiveGroupId,
          orderedGroups,
        );
        isGroupMissed = isBeforeCurrent && !isGroupCompleted;
      }

      return (
        <>
          <div className={styles.section}>
            {node.groupName && (
              <p
                className={styles.groupName}
                data-completed={isGroupCompleted}
                data-current={isCurrentNode}
                data-missed={isGroupMissed}
                id={node.groupId?.toString() ?? ""}
              >
                {node.groupName}
              </p>
            )}
            <div className={styles.nameContainer} data-current={isCurrentNode ? "true" : "false"}>
              <div className={styles.textContainer}>
                <p
                  className={clsx(styles.name, node.isVerbatim && styles.verbatim)}
                  data-current={isCurrentNode ? (isFound ? "found" : "true") : "false"}
                  id={node.id.toString()}
                  ref={isCurrentNode ? currentRef : null}
                >
                  {node.displayName}
                </p>
                {node.isVerbatim && (
                  <div className={styles.verbatim}>
                    <VerbatimIcon />
                  </div>
                )}
              </div>
              <div className={styles.iconContainer}>
                {node.isSales && (
                  <div className={styles.sale}>
                    <SaleIcon />
                  </div>
                )}
                {node.isCompliance && (
                  <div className={styles.compliance}>
                    <ComplianceIcon />
                  </div>
                )}
              </div>
            </div>
            <p className={styles.description} data-current={isCurrentNode ? "true" : "false"}>
              {node.description}
            </p>

            <div className={styles.parentProgressContainer}>
              {isCurrentNode && currentNodeId === autoFoundNodeId && (
                <TimedProgressBar node={node} onSelectOption={onSelectOption} />
              )}
            </div>
          </div>
          <Node
            autoFoundNodeId={autoFoundNodeId}
            completedNodeIds={completedNodeIds}
            currentNodeId={currentNodeId}
            data={node.children}
            flattenedGraph={flattenedGraph}
            onProceed={onProceed}
            onSelectOption={onSelectOption}
            orderedGroups={orderedGroups}
          />
        </>
      );
    }
  }

  if (data.every((node) => node.elementType === ElementType.OPTION)) {
    const chosenOption = data.find((node) => completedNodeIds?.has(node.id));

    const isActiveOption = data.some((node) => node.id === currentNodeId);

    return (
      <>
        <div className={styles.options}>
          <p>The customer responded with:</p>
          <div className={styles.optionsList}>
            {data.map((node) => {
              return (
                <button
                  className={styles.option}
                  disabled={!isActiveOption}
                  data-chosen={completedNodeIds?.has(node.id)}
                  key={node.id}
                  onClick={() => onSelectOption?.(node.id.toString())}
                  type="button"
                >
                  <p>{node.displayName}</p>
                </button>
              );
            })}
          </div>
        </div>
        {chosenOption && (
          <Node
            autoFoundNodeId={autoFoundNodeId}
            completedNodeIds={completedNodeIds}
            currentNodeId={currentNodeId}
            data={chosenOption.children}
            flattenedGraph={flattenedGraph}
            onProceed={onProceed}
            onSelectOption={onSelectOption}
            orderedGroups={orderedGroups}
          />
        )}
      </>
    );
  }

  return (
    <>
      {data.map((node) => (
        <Node
          autoFoundNodeId={autoFoundNodeId}
          completedNodeIds={completedNodeIds}
          currentNodeId={currentNodeId}
          data={node.children}
          flattenedGraph={flattenedGraph}
          key={node.id}
          onProceed={onProceed}
          onSelectOption={onSelectOption}
          orderedGroups={orderedGroups}
        />
      ))}
    </>
  );
}
